import { api, unwrap } from '@/api/client';
import type { components, operations } from '@/api/schema';

/** Lee un archivo del navegador como base64 (sin el prefijo `data:`), para subirlo en JSON. */
export function leerBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = typeof r.result === 'string' ? r.result : '';
      resolve(s.slice(s.indexOf(',') + 1));
    };
    r.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    r.readAsDataURL(file);
  });
}

export const MAX_IMAGEN_MB = 5;

export function validarImagen(file: File): string | null {
  if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
    return 'Solo se aceptan imágenes JPG, PNG, WebP o GIF.';
  }
  if (file.size > MAX_IMAGEN_MB * 1024 * 1024) return `La imagen pesa más de ${MAX_IMAGEN_MB} MB.`;
  return null;
}

export const MAX_PDF_MB = 30;
export type TipoArchivo = 'image' | 'pdf';
export const ACEPTA: Record<TipoArchivo, string> = {
  image: 'image/jpeg,image/png,image/webp,image/gif',
  pdf: 'application/pdf',
};

/** Imagen (≤5 MB) o PDF (≤30 MB), según lo que acepte el destino. */
export function validarArchivo(file: File, tipos: TipoArchivo[]): string | null {
  if (file.type === 'application/pdf' && tipos.includes('pdf')) {
    return file.size > MAX_PDF_MB * 1024 * 1024 ? `El PDF pesa más de ${MAX_PDF_MB} MB.` : null;
  }
  if (tipos.includes('image') && file.type.startsWith('image/')) return validarImagen(file);
  return tipos.includes('image') && tipos.includes('pdf')
    ? 'Solo se aceptan imágenes (JPG, PNG, WebP o GIF) o PDF.'
    : tipos.includes('pdf')
      ? 'Solo se aceptan archivos PDF.'
      : 'Solo se aceptan imágenes JPG, PNG, WebP o GIF.';
}

/** Nombre sugerido a partir del archivo: sin extensión ni guiones bajos. */
export function nombreDeArchivo(file: File): string {
  return file.name
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/[_-]+/g, ' ')
    .trim();
}

type CamposSubida = operations['postAdminUploads']['requestBody']['content']['multipart/form-data'];
export type DestinoSubida = CamposSubida['target'];
export type ArchivoSubido = components['schemas']['UploadResult'];

/**
 * Sube un archivo al CDN (`POST /admin/uploads`, multipart) y devuelve su ruta. El tipo lo valida
 * la API por el contenido; aquí solo se evita mandar algo que de seguro va a rechazar.
 */
export function subirArchivo(
  file: File,
  target: DestinoSubida,
  ownerId?: number,
): Promise<ArchivoSubido> {
  return unwrap<ArchivoSubido>(
    api.POST('/api/v1/admin/uploads', {
      body: { target, ownerId, file: file as unknown as string },
      bodySerializer: (b) => {
        const fd = new FormData();
        fd.set('target', b.target);
        if (b.ownerId !== undefined) fd.set('ownerId', String(b.ownerId));
        fd.set('file', file);
        return fd;
      },
    }),
  );
}
